import {
  ForumCodeDocIcon,
  ForumCodeNotebookIcon,
  ForumDocIcon,
  ForumWorkbookIcon,
  GoogleDocIcon,
  GoogleDrawingIcon,
  GoogleSheetIcon,
  LinkIcon,
  NotesIcon,
  PdfIcon,
  ScreenShareIcon,
  VideoIcon,
  WhiteboardIcon,
} from "@minervaproject/fds";
import { Dispatch, UnknownAction } from "redux";

import { GOOGLE_DRIVE_DOMAIN } from "config";
import { setResourceOwnerId } from "shared/reducers/classroom";
import {
  BreakoutRoomType,
  GoogleMimetypeType,
  MinervaResourceSubtypeType,
  MuxDetailsType,
  ResourceType,
  ResourceTypeType,
} from "shared/types";
import { UserType } from "shared/types/user";

export const RESOURCE_NAMES: { [type: string]: string } = {
  [ResourceTypeType.File]: "Files",
  [ResourceTypeType.GoogleDrive]: "Google Drive Resources",
  [ResourceTypeType.HtmlSlide]: "HTML Slides",
  [ResourceTypeType.ForumDoc]: "Forum Docs",
  [ResourceTypeType.ForumWorkbook]: "Forum Workbooks",
  [ResourceTypeType.Pdf]: "PDFs",
  [ResourceTypeType.Url]: "Embedded Websites",
  [ResourceTypeType.Video]: "Videos",
  [ResourceTypeType.Whiteboard]: "Whiteboards",
};

export const CLASSROOM_RESOURCE_TYPES = [
  ResourceTypeType.File,
  ResourceTypeType.GoogleDrive,
  ResourceTypeType.HtmlSlide,
  ResourceTypeType.ForumDoc,
  ResourceTypeType.ForumWorkbook,
  ResourceTypeType.Pdf,
  ResourceTypeType.Screenshare,
  ResourceTypeType.Url,
  ResourceTypeType.Video,
  ResourceTypeType.Whiteboard,
];

export const getResourceIcon = (resource: ResourceType) => {
  switch (resource.type) {
    case ResourceTypeType.File:
      if (resource.url.toLowerCase().endsWith(".mp4")) return VideoIcon;
      return NotesIcon;
    case ResourceTypeType.GoogleDrive: {
      const googleMimetype = getGoogleMimetype(resource);
      if (googleMimetype === GoogleMimetypeType.Document) return GoogleDocIcon;
      if (googleMimetype === GoogleMimetypeType.Drawing) return GoogleDrawingIcon;
      if (googleMimetype === GoogleMimetypeType.Spreadsheet) return GoogleSheetIcon;
      return NotesIcon;
    }
    case ResourceTypeType.HtmlSlide:
      return NotesIcon;
    case ResourceTypeType.ForumDoc: {
      const subtype = resource.subtype;
      if (subtype === MinervaResourceSubtypeType.ForumCodeDoc) return ForumCodeDocIcon;
      if (subtype === MinervaResourceSubtypeType.ForumDoc) return ForumDocIcon;
      return NotesIcon;
    }
    case ResourceTypeType.Pdf:
      return PdfIcon;
    case ResourceTypeType.ForumWorkbook: {
      const subtype = resource.subtype;
      if (subtype === MinervaResourceSubtypeType.ForumCodeWorkbook) return ForumCodeNotebookIcon;
      if (subtype === MinervaResourceSubtypeType.ForumWorkbook) return ForumWorkbookIcon;
      return NotesIcon;
    }
    case ResourceTypeType.Url:
      return LinkIcon;
    case ResourceTypeType.Whiteboard:
      return WhiteboardIcon;
    case ResourceTypeType.Video:
      return VideoIcon;
    case ResourceTypeType.Screenshare:
      return ScreenShareIcon;
    default:
      return NotesIcon;
  }
};

export function getGoogleMimetype(resource: ResourceType): GoogleMimetypeType | undefined {
  if (resource.type === ResourceTypeType.GoogleDrive) {
    return resource.google_mimetype.replace(
      "application/vnd.google-apps.",
      "",
    ) as GoogleMimetypeType;
  }
}

export function getGoogleDriveUrl(resource: ResourceType) {
  if (resource.type !== ResourceTypeType.GoogleDrive) {
    window.flightLogger.warn(
      `[getGoogleDriveUrl] Attempting to generate URL for non-Drive resource ${resource.id}`,
    );
    return;
  }

  if (!resource.external_id) {
    window.flightLogger.warn(
      `[getGoogleDriveUrl] Resource ${resource.id} does not have external_id`,
    );
    return;
  }

  const urlPrefix = `https://docs.google.com/a/${GOOGLE_DRIVE_DOMAIN}`;

  if (getGoogleMimetype(resource) === GoogleMimetypeType.Document) {
    return `${urlPrefix}/document/d/${resource.external_id}`;
  }
  if (getGoogleMimetype(resource) === GoogleMimetypeType.Spreadsheet) {
    return `${urlPrefix}/spreadsheets/d/${resource.external_id}`;
  }
  if (getGoogleMimetype(resource) === GoogleMimetypeType.Drawing) {
    return `${urlPrefix}/drawings/d/${resource.external_id}`;
  }

  window.flightLogger.warn(
    `[getGoogleDriveUrl] Resource ${resource.id} has unknown Google mimetype ${resource.google_mimetype}`,
  );
}

export function isClassroomResource(resource: ResourceType) {
  if (
    resource.type === ResourceTypeType.ForumDoc &&
    resource.subtype === MinervaResourceSubtypeType.CaptionsDoc
  )
    return false;
  return CLASSROOM_RESOURCE_TYPES.includes(resource.type);
}

export function isVisibleToStudents(resource: ResourceType) {
  return resource.preclass;
}

export function isGeneratedResource(resource: ResourceType) {
  return (
    resource.type === ResourceTypeType.ForumDoc || resource.type === ResourceTypeType.Whiteboard
  );
}

export function isMuxVideo(
  type: ResourceTypeType,
  url: string,
  muxDetails: MuxDetailsType | undefined,
) {
  return isVideoFile(type, url) && !!muxDetails?.playback_id;
}

export function isVideoFile(type: ResourceTypeType, url: string) {
  return type === ResourceTypeType.File && url.toLowerCase().endsWith(".mp4");
}

export function makeValidUrlOrFalse(str: string): string | false {
  // From https://gist.github.com/dperini/729294, which is from https://mathiasbynens.be/demo/url-regex
  const isValidPattern = new RegExp(
    "^" +
      // protocol identifier (optional)
      // short syntax // still required
      "(?:(?:(?:https?|ftp):)?\\/\\/)" +
      // user:pass BasicAuth (optional)
      "(?:\\S+(?::\\S*)?@)?" +
      "(?:" +
      // IP address exclusion
      // private & local networks
      "(?!(?:10|127)(?:\\.\\d{1,3}){3})" +
      "(?!(?:169\\.254|192\\.168)(?:\\.\\d{1,3}){2})" +
      "(?!172\\.(?:1[6-9]|2\\d|3[0-1])(?:\\.\\d{1,3}){2})" +
      // IP address dotted notation octets
      // excludes loopback network 0.0.0.0
      // excludes reserved space >= 224.0.0.0
      // excludes network & broadcast addresses
      // (first & last IP address of each class)
      "(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])" +
      "(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}" +
      "(?:\\.(?:[1-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))" +
      "|" +
      // host & domain names, may end with dot
      // can be replaced by a shortest alternative
      // (?![-_])(?:[-\\w\\u00a1-\\uffff]{0,63}[^-_]\\.)+
      "(?:" +
      "(?:" +
      "[a-z0-9\\u00a1-\\uffff]" +
      "[a-z0-9\\u00a1-\\uffff_-]{0,62}" +
      ")?" +
      "[a-z0-9\\u00a1-\\uffff]\\." +
      ")+" +
      // TLD identifier name, may end with dot
      "(?:[a-z\\u00a1-\\uffff]{2,}\\.?)" +
      ")" +
      // port number (optional)
      "(?::\\d{2,5})?" +
      // resource path (optional)
      "(?:[/?#]\\S*)?" +
      "$",
    "i",
  );
  let trimmedStr = str.trim();
  const isValid = isValidPattern.test(trimmedStr);
  if (isValid) {
    return trimmedStr;
  }
  if (trimmedStr.toLowerCase().indexOf("http://") === -1) {
    trimmedStr = `http://${trimmedStr}`;
    return makeValidUrlOrFalse(trimmedStr);
  }
  return false;
}

/**
 * Return true if the resource represents an object in a private S3
 * bucket, which implies that a signed url is required to download
 * it. Otherwise, return false.
 *
 * Both the type and the url of the resource are checked in order to
 * ensure that we do url-signing just for the resources that need it,
 * and not for any that don't. Testing the url against a hard-coded
 * hostname is somewhat brittle and not very intention-revealing, but
 * hopefully voluminous documentation will make up for that.
 *
 * Resources that need url-signing at the time of this writing are
 * those that are created via upload from the assignment page when
 * a user (student) uploads an assignment submission, or from the
 * classroom page when a user (any) uploads a resource. These actions
 * result in resources of type Resource.Type.pdf with a url that begins
 * with "https://s3.amazonaws.com/picasso.box_resources/ or
 * "https://s3.us-east-1.amazonaws.com/picasso.box_resources/" or
 * "https://s3.me-central-1.amazonaws.com/picasso-file-resources-uae/" or
 * "https://picasso-file-resources-uae.s3.me-central-1.amazonaws.com/".
 * Since the "picasso.box_resources" bucket name contains a ".",
 * we use a path-style URL.
 *
 * Resources that _don't_ need url-signing include those that are created
 * via publishing a lesson plan from coursebuilder. This results in
 * resources of type Resource.Type.pdf, but with a url starting with
 * "https://course-resources.minerva.edu/". That hostname refers to
 * a public bucket, so those urls don't need to be signed.
 *
 * We ought to come up with a more formalized representation of the
 * information we need here. The server should already know whether
 * a resource needs signing or not, and should tell the client so.
 * See https://app.asana.com/0/32427891253841/1126741364755645/f
 * for more history on the problem. (JN)
 */
export function needsSignatureToAccess(type: ResourceTypeType, url: string) {
  const legacyS3UrlRegex = /https:\/\/s3\.amazonaws\.com\/.*/;
  const regionalS3UrlRegex = /https:\/\/([a-z0-9-]+\.)?s3\.\w{2}-\w+-\d\.amazonaws\.com\/.*/;

  return (
    (type === ResourceTypeType.Pdf || isVideoFile(type, url)) &&
    (legacyS3UrlRegex.test(url) || regionalS3UrlRegex.test(url))
  );
}

// If a resource does not have an owner, find the first present user and assign them as the owner.
// If a resource does have an owner, find another present user and assign them as the owner.
// If in a breakout, the owner should be in the same breakout room as the resource.
export function assignNewOwnerToResource(
  resource: ResourceType,
  currentUserId: string,
  users: UserType[],
  dispatch: Dispatch<UnknownAction>,
  canControlClassroomUserIds: string[] = [],
  isInBreakout: boolean = false,
  breakoutRooms: BreakoutRoomType[] = [],
) {
  const canControlClassroomUsers = users.filter((user) =>
    canControlClassroomUserIds.includes(user.id),
  );

  const maybeSetResourceOwnerId = (newOwner: UserType | undefined) => {
    if (newOwner && newOwner.id === currentUserId) {
      window.flightLogger.log(
        `[assignNewOwnerToResource] owner_id for resource ${resource.id} is ${resource.owner_id}, assigning new owner ${newOwner.id}`,
      );
      dispatch(setResourceOwnerId({ resourceId: resource.id, ownerId: newOwner.id }));
    } else if (!newOwner) {
      window.flightLogger.warn(
        `[assignNewOwnerToResource] No owner found for resource ${resource.id}`,
      );
    }
  };

  if (isInBreakout) {
    const breakoutRoom = breakoutRooms.find(({ resource_ids }) =>
      resource_ids.includes(resource.id),
    );
    if (breakoutRoom) {
      const assignedUsers = users.filter(({ id }) => breakoutRoom?.assigned_user_ids.includes(id));
      const visitingUsers = users.filter(({ id }) => breakoutRoom?.visiting_user_ids.includes(id));
      const firstPresentUser =
        assignedUsers.find((user) => user.is_present && user.id !== resource.owner_id) ||
        visitingUsers.find((user) => user.is_present && user.id !== resource.owner_id) ||
        canControlClassroomUsers.find((user) => user.is_present && user.id !== resource.owner_id);
      maybeSetResourceOwnerId(firstPresentUser);
    } else {
      window.flightLogger.warn(
        `[assignNewOwnerToResource] No owner set for resource ${resource.id} because no breakout room was found`,
      );
    }
  } else {
    const firstPresentUser = canControlClassroomUsers.find(
      (user) => user.is_present && user.id !== resource.owner_id,
    );
    maybeSetResourceOwnerId(firstPresentUser);
  }
}
